import type { MvcLocation } from "@repo/models";

import { useCloudSettingsQuery, useVerbiageQuery, useUserContext } from "@repo/baseCloud";
import { TLPGrid, useGridColumns } from "@repo/baseCloud/components";
import { hasValue, useSearchParams } from "@repo/helpers";
import { Button, ButtonColor, Loading, MoveableDialog } from "@repo/ui";
import { useCallback, useEffect, useState } from "react";

import type { LocationsFilter } from "../../models";

import LocationsApi, { controller } from "../../api/locationsApi";
import { LocationPortal } from "../../apps/locationPortal";
import { getInitialDialogSize } from "../../helpers/locationPortal";
import { LocationScheduleImportModal } from "./components/LocationScheduleImportModal";
import { LocationsImportModal } from "./components/LocationsImportModal";
import { useLocationsData } from "./hooks";

interface Props {
	filters: LocationsFilter;
	setFilters: (filters: LocationsFilter) => void;
}

export function LocationsGrid(props: Props) {
	const { settings, settingsLoading } = useCloudSettingsQuery();
	const { verbiage } = useVerbiageQuery();
	const { user } = useUserContext();

	const [dialogOpen, setDialogOpen] = useState<"edit" | "summary" | null>(null);
	const [locationIdForPortal, setLocationIdForPortal] = useState<number | null>(null);
	const [hasAutoOpened, setHasAutoOpened] = useState(false);

	const searchParams = useSearchParams();
	const locationIdParam = searchParams.get("locationId");

	useEffect(() => {
		if (locationIdParam && !hasAutoOpened) {
			const id = parseInt(locationIdParam);
			if (!isNaN(id)) {
				setLocationIdForPortal(id);
				setDialogOpen("summary");
				setHasAutoOpened(true);
			}
		}
	}, [locationIdParam, hasAutoOpened]);

	const handleDialogOpenChange = (open: boolean) => {
		setDialogOpen(open ? "summary" : null);
		if (!open) {
			setLocationIdForPortal(null);
		}
	};

	const openPortalModal = useCallback((locationId: number) => {
		setLocationIdForPortal(locationId);
		setDialogOpen("summary");
	}, []);

	const openPortalModalNew = () => {
		setLocationIdForPortal(null);
		setDialogOpen("edit");
	};

	const { columns, isLoading: isLoadingColumns } = useGridColumns<MvcLocation>(
		"Locations",
		LocationsApi.getGridColumns,
		{ openPortalModal },
		settings?.locations,
	);

	const { locationsQueryProxy, refetch } = useLocationsData(props.filters);
	const [importOpen, setImportOpen] = useState(false);
	const [locationsImportOpen, setLocationsImportOpen] = useState(false);

	if (isLoadingColumns) return null;

	return (
		<Loading loading={settingsLoading}>
			<TLPGrid<MvcLocation, LocationsFilter>
				id="locationsGrid"
				title={`${verbiage.location}s`}
				keyProperty="locationId"
				filters={props.filters}
				setFilters={props.setFilters}
				query={locationsQueryProxy}
				columns={columns}
				isEnriching={locationsQueryProxy.isEnriching}
				refresh={refetch}
				apiRoute={controller}
				createButtonText={user?.userIsAdmin ? `Create New ${verbiage.location}` : undefined}
				onCreateClick={user?.userIsAdmin ? openPortalModalNew : undefined}
				gridApi={LocationsApi}
				exportFunc={LocationsApi.getLocations}
				pageSizes={[10, 25, 50]}
				selected={locationIdForPortal ? [locationIdForPortal] : []}
				additionalActions={
					<>
						<Button
							color={ButtonColor.Green}
							onClick={() => setLocationsImportOpen(true)}
							hidden={!settings?.locations.showUpload}
						>
							Upload {verbiage.location} File
						</Button>
						<Button
							color={ButtonColor.Green}
							onClick={() => setImportOpen(true)}
							hidden={!settings?.locations.showImportSchedule}
						>
							Import {verbiage.location} Schedule
						</Button>
					</>
				}
			/>
			<LocationScheduleImportModal open={importOpen} onOpenChange={setImportOpen} />
			<LocationsImportModal
				open={locationsImportOpen}
				onOpenChange={setLocationsImportOpen}
				onSuccess={refetch}
			/>
			<MoveableDialog
				open={hasValue(dialogOpen)}
				onOpenChange={handleDialogOpenChange}
				title={settings?.portalTitle}
				initialSize={getInitialDialogSize()}
				resizable
				movable
				className="location-portal-modal !p-0"
				contentClassName="!p-0"
			>
				<LocationPortal
					locationId={locationIdForPortal}
					location={locationsQueryProxy.data?.items?.find((item) => item.locationId === locationIdForPortal)}
					selectedTab={dialogOpen}
				/>
			</MoveableDialog>
		</Loading>
	);
}
